import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';

const ServiceArea = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const areas = [
    'Gaithersburg',
    'Rockville',
    'Bethesda',
    'Potomac',
    'Silver Spring',
    'Germantown',
    'Clarksburg',
    'Damascus',
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="service-area"
      data-testid="service-area-section"
      ref={ref}
      className="section-padding bg-white"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
              Service Area
            </p>
            <h2
              data-testid="service-area-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-bold mb-6 leading-tight"
            >
              Serving Maryland
              <br />
              Homeowners
            </h2>
            <div className="w-16 h-0.5 bg-[#D4AF37] mb-8" />
            <p className="text-[#71717A] text-base md:text-lg leading-relaxed mb-8">
              Lux Renovations proudly serves homeowners throughout Montgomery
              County and surrounding Maryland areas. Our team brings expert
              craftsmanship and personalized service to every community we serve.
            </p>

            {/* Areas Grid */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {areas.map((area, index) => (
                <motion.div
                  key={area}
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex items-center gap-2 text-[#1A1A1A]"
                >
                  <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-sm font-medium">{area}</span>
                </motion.div>
              ))}
            </div>

            <p className="text-[#71717A] text-sm mb-8">
              Don't see your area listed? Contact us — we may still be able to
              serve your location.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToContact}
                data-testid="service-area-cta"
                className="bg-[#0A0A0A] text-white hover:bg-[#27272A] rounded-none px-8 py-6"
              >
                Schedule a Consultation
              </Button>
              <a
                href="tel:2403709029"
                className="flex items-center justify-center gap-2 px-8 py-3 border-2 border-[#E4E4E7] text-[#0A0A0A] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (240) 370-9029
              </a>
            </div>
          </motion.div>

          {/* Map Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-[#F4F4F5] p-8 md:p-12">
              {/* Decorative Map Background */}
              <div className="aspect-square relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6">
                      <MapPin className="w-16 h-16 text-[#D4AF37]" />
                    </div>
                    <p className="font-serif text-2xl text-[#0A0A0A] font-bold mb-2">
                      Montgomery County
                    </p>
                    <p className="text-[#71717A]">& Surrounding Areas</p>
                  </div>
                </div>
                {/* Decorative Circles */}
                <div className="absolute inset-0">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full border border-[#D4AF37]/20 rounded-full" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 border border-[#D4AF37]/30 rounded-full" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 border border-[#D4AF37]/40 rounded-full" />
                </div>
              </div>
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#D4AF37]" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37]" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServiceArea;
